/**
 * Goods Receipt React Hooks
 * Epic 21: Bevételezés (Goods Receipt Management)
 *
 * TanStack Query hooks for avizo, receipt, and discrepancy CRUD.
 */

import {
  cancelAvizo,
  completeReceipt,
  createAvizo,
  createDiscrepancy,
  createReceipt,
  getAvizoById,
  getDiscrepancies,
  getPendingAvizos,
  getReceiptById,
  getReceiptItems,
  resolveDiscrepancy,
  type Avizo,
  type CreateAvizoInput,
  type CreateDiscrepancyInput,
  type CreateReceiptInput,
  type Discrepancy,
  type Receipt,
  type ReceiptItem,
  type ResolveDiscrepancyInput,
} from '@/api/goods-receipt';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// AVIZO QUERIES
// ============================================

export function usePendingAvizos() {
  const query = useQuery({
    queryKey: ['goods-receipt', 'avizos', 'pending'],
    queryFn: async () => {
      const res = await getPendingAvizos();
      return res.data;
    },
  });

  return {
    avizos: (query.data ?? []) as Avizo[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useAvizo(id: string | undefined) {
  const query = useQuery({
    queryKey: ['goods-receipt', 'avizos', 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getAvizoById(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    avizo: query.data as Avizo | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// RECEIPT QUERIES
// ============================================

export function useReceipt(id: string | undefined) {
  const query = useQuery({
    queryKey: ['goods-receipt', 'receipts', 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getReceiptById(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    receipt: query.data as Receipt | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useReceiptItems(id: string | undefined) {
  const query = useQuery({
    queryKey: ['goods-receipt', 'receipts', 'items', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getReceiptItems(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    items: (query.data ?? []) as ReceiptItem[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// DISCREPANCY QUERIES
// ============================================

export function useDiscrepancies(receiptId: string | undefined, unresolvedOnly?: boolean) {
  const query = useQuery({
    queryKey: ['goods-receipt', 'discrepancies', receiptId, { unresolvedOnly }],
    queryFn: async () => {
      if (!receiptId) throw new Error('No receipt ID');
      const res = await getDiscrepancies(receiptId, unresolvedOnly);
      return res.data;
    },
    enabled: !!receiptId,
  });

  return {
    discrepancies: (query.data ?? []) as Discrepancy[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// MUTATIONS
// ============================================

export function useCreateAvizo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAvizoInput) => createAvizo(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goods-receipt', 'avizos'] });
    },
  });
}

export function useCancelAvizo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelAvizo(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goods-receipt', 'avizos'] });
    },
  });
}

export function useCreateReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReceiptInput) => createReceipt(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goods-receipt'] });
    },
  });
}

export function useCompleteReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => completeReceipt(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goods-receipt'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useCreateDiscrepancy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { receiptId: string; input: CreateDiscrepancyInput }) =>
      createDiscrepancy(data.receiptId, data.input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goods-receipt'] });
    },
  });
}

export function useResolveDiscrepancy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      receiptId: string;
      discrepancyId: string;
      input: ResolveDiscrepancyInput;
    }) => resolveDiscrepancy(data.receiptId, data.discrepancyId, data.input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goods-receipt'] });
    },
  });
}
